import type { GeneratedGHGInventoryReport } from '../types';
import { formatPerCapitaMt, formatSharePercent, formatSignedPercent, formatWholeMt, percentChange } from '../formatters';
import { findingLabelForSector } from '../memberLanguage';
import { isAgriculturePlaceholderZero, sectorShare, transportBuildingShares } from '../sectorPresentation';
import {
  derivedSameRun,
  isPublishableNumeric,
  liveClaim,
  numericForMemberProse,
  provenance,
  readClaim,
  scaledOtherYearClaim,
  suppressedByPolicyClaim,
  unavailableClaim,
  type ClaimValue,
} from './claimValue';
import {
  classifyWastewaterState,
  electricityLooksLikeFill,
  extractStoredWastewaterMt,
  extractWastewaterSourceYear,
  sectorByCode,
  unusualDenominator,
} from './classifiers';
import { INTRO_COPY } from './copy';
import { jurisdictionIntroRegistry } from './jurisdictionRegistry';
import type { IntroFinding, IntroductionBlockId, IntroductionView, ScopeBoxRow } from './types';
import { ACCOUNTING_LENS_LABEL } from './types';

function wordCount(parts: string[]): number {
  return parts
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean).length;
}

function formatComponentMt(value: number): string {
  if (!Number.isFinite(value)) return '';
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.005) return formatWholeMt(value);
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function includedSourceLabels(report: GeneratedGHGInventoryReport): string {
  const labels: string[] = [];
  const has = (code: string) => {
    const s = sectorByCode(report.sectorDetail, code);
    return s?.emissionsMt.value != null;
  };
  if (has('ldv') || has('hdv')) labels.push('On-road travel');
  if (has('elec_res') || has('elec_com') || has('ng_res') || has('ng_com')) labels.push('building energy');
  if (has('waste')) labels.push('waste and wastewater');
  if (has('offroad')) labels.push('off-road equipment');
  return labels.length ? labels.join('; ') : INTRO_COPY.includedSourcesDefault;
}

export function assembleIntroduction(report: GeneratedGHGInventoryReport): IntroductionView {
  const year = report.identity.inventoryYear;
  const name = report.identity.jurisdiction.canonicalName;
  const county = report.identity.jurisdiction.county;
  const geofips = report.identity.jurisdiction.populationGeofips;
  const registry = jurisdictionIntroRegistry(geofips);
  const liveProv = provenance('shared_ghg_fetch', 'live community inventory stack');
  const popProv = provenance('population_table', 'population for inventory year');
  const derivedProv = provenance('derived', 'publishable live inputs');
  const wwProv = provenance('wastewater_inventory', 'stored wastewater inventory scaled to inventory year');

  const rawTotal = report.sectorSummary.totalEmissionsMt.value;
  const communityTotal: ClaimValue<number> =
    rawTotal != null && Number.isFinite(rawTotal)
      ? liveClaim(rawTotal, year, liveProv)
      : unavailableClaim(year, liveProv, 'No community inventory total');

  const rawPop = report.communityProfile.population?.value;
  const population: ClaimValue<number> =
    typeof rawPop === 'number' && Number.isFinite(rawPop)
      ? readClaim(rawPop, year, popProv)
      : unavailableClaim(year, popProv, 'Population unavailable');

  const unusual = unusualDenominator(report);
  const fillElec = electricityLooksLikeFill(report.sectorDetail);
  let perResident: ClaimValue<number>;
  if (unusual) {
    perResident = suppressedByPolicyClaim<number>(null, year, derivedProv, 'Unusual resident denominator');
  } else if (fillElec) {
    perResident = suppressedByPolicyClaim<number>(null, year, derivedProv, 'Electricity looks like a filled missing value');
  } else if (isPublishableNumeric(communityTotal) && isPublishableNumeric(population) && population.value > 0) {
    perResident = derivedSameRun(
      communityTotal.value / population.value,
      [communityTotal, population],
      year,
      derivedProv
    );
  } else {
    perResident = unavailableClaim(year, derivedProv, 'Per-resident requires total and population');
  }

  const agPlaceholder = isAgriculturePlaceholderZero(report.sectorDetail);
  const missingTotal = !isPublishableNumeric(communityTotal);
  const wwState = classifyWastewaterState(report);
  const wwSector = sectorByCode(report.sectorDetail, 'water_wastewater');
  const wasteSector = sectorByCode(report.sectorDetail, 'waste');
  const wwSourceYear = extractWastewaterSourceYear(wwSector);
  const storedWw = extractStoredWastewaterMt(wwSector);

  let wastewater: ClaimValue<number>;
  if (wwState === 'unresolved_or_conflicting') {
    wastewater = unavailableClaim(year, wwProv, 'Wastewater unresolved or conflicting');
  } else if (
    (wwState === 'combined_scaled_other_year_in_total' || wwState === 'available_different_year_not_in_total') &&
    wwSector?.emissionsMt.value != null &&
    wwSourceYear != null &&
    wwSourceYear !== year
  ) {
    wastewater = scaledOtherYearClaim(
      wwSector.emissionsMt.value,
      year,
      wwSourceYear,
      `Scaled estimate from the ${wwSourceYear} stored inventory, not a new ${year} observation`,
      wwProv
    );
  } else if (wwSector?.emissionsMt.value != null && Number.isFinite(wwSector.emissionsMt.value)) {
    wastewater = liveClaim(wwSector.emissionsMt.value, year, wwProv, {
      sourceYear: wwSourceYear ?? year,
      qualification: 'Wastewater component of the combined waste and wastewater slice',
    });
  } else {
    wastewater = unavailableClaim(year, wwProv, 'No wastewater component');
  }

  const purposeParagraphs = [INTRO_COPY.purposeLead(name, year), INTRO_COPY.purposeLimits];
  const aboutParagraphs: string[] = [];
  const popNum = numericForMemberProse(population);
  if (popNum != null) {
    const popLabel = Math.round(popNum).toLocaleString('en-US');
    aboutParagraphs.push(
      unusual
        ? INTRO_COPY.aboutPopulationNoPerResident(name, year, popLabel)
        : INTRO_COPY.aboutPopulation(name, year, popLabel)
    );
    if (county && county.trim() && !name.toLowerCase().includes(county.toLowerCase())) {
      aboutParagraphs.push(INTRO_COPY.aboutWithCounty(name, county));
    }
  } else {
    aboutParagraphs.push(INTRO_COPY.aboutNoPopulation(name, year));
  }

  const scopeParagraphs = [INTRO_COPY.scopeGeography(name), INTRO_COPY.scopeMethodsDiffer];
  for (const boundary of registry?.unresolvedBoundaries ?? []) {
    scopeParagraphs.push(...boundary.block3Paragraphs);
  }

  const findings: IntroFinding[] = [];
  if (unusual) {
    findings.push({
      id: 'unusual_denominator',
      kind: 'qualitative',
      copyKey: 'unusual_denominator',
      text: INTRO_COPY.unusualDenominator,
    });
  }
  if ((registry?.unresolvedBoundaries.length ?? 0) > 0) {
    findings.push({
      id: 'boundary_unresolved',
      kind: 'qualitative',
      copyKey: 'boundary_unresolved',
      text: registry!.unresolvedBoundaries[0].scopeBoxLine,
    });
  }

  const headlineParagraphs: string[] = [];
  const totalNum = numericForMemberProse(communityTotal);
  const perNum = numericForMemberProse(perResident);
  if (totalNum != null) {
    headlineParagraphs.push(
      perNum != null
        ? INTRO_COPY.headlineTotalPerResident(name, year, formatWholeMt(totalNum), formatPerCapitaMt(perNum))
        : INTRO_COPY.headlineTotal(name, year, formatWholeMt(totalNum))
    );
  }

  const bt = transportBuildingShares(report.sectorDetail, totalNum);
  const transportOk =
    totalNum != null &&
    bt.transportShare != null &&
    bt.buildingShare != null &&
    !fillElec;

  if (transportOk && findings.filter((f) => f.kind === 'quantitative').length < 2) {
    const text = `${INTRO_COPY.transportVsBuildings(
      formatSharePercent(bt.transportShare as number),
      formatSharePercent(bt.buildingShare as number)
    )} ${INTRO_COPY.ldvQualitativeLargest}`;
    findings.push({
      id: 'transport_vs_buildings',
      kind: 'quantitative',
      copyKey: 'transport_vs_buildings',
      text,
    });
    headlineParagraphs.push(text);
  } else if (!transportOk && totalNum != null && findings.filter((f) => f.kind === 'quantitative').length < 2) {
    const dominant = report.sectorSummary.dominantSector;
    const row = report.sectorDetail.find((s) => s.sectorLabel === dominant);
    const share = sectorShare(row?.emissionsMt.value ?? null, totalNum);
    if (dominant && share != null && row?.sectorCode !== 'agriculture') {
      const label = findingLabelForSector(row?.sectorCode, dominant);
      const text = INTRO_COPY.dominantNumeric(label, formatSharePercent(share));
      findings.push({
        id: 'dominant_source_numeric',
        kind: 'quantitative',
        copyKey: 'dominant_source_numeric',
        text,
      });
      headlineParagraphs.push(text);
    }
  } else if (transportOk) {
    findings.push({
      id: 'dominant_source_qualitative',
      kind: 'qualitative',
      copyKey: 'ldv_qualitative',
      text: INTRO_COPY.ldvQualitativeLargest,
    });
  }

  const y2005 = report.historical.points.find((p) => p.year === 2005)?.totalEmissionsMt.value ?? null;
  const yNow = report.historical.points.find((p) => p.year === year)?.totalEmissionsMt.value ?? totalNum;
  if (
    report.historical.displayAllowed &&
    y2005 != null &&
    yNow != null &&
    findings.filter((f) => f.kind === 'quantitative').length < 2
  ) {
    const base = liveClaim(y2005, 2005, liveProv);
    const current = liveClaim(yNow, year, liveProv);
    const delta = percentChange(y2005, yNow);
    if (delta != null && isPublishableNumeric(base) && isPublishableNumeric(current)) {
      const signed = formatSignedPercent(delta);
      const changePhrase =
        delta < 0 ? `${signed.replace('−', '')} lower` : delta > 0 ? `${signed.replace('+', '')} higher` : 'unchanged';
      const text = INTRO_COPY.historicalSoftwareSeries(String(year), changePhrase);
      findings.push({
        id: 'historical_software_series',
        kind: 'quantitative',
        copyKey: 'historical_software_series',
        text,
      });
      headlineParagraphs.push(text);
    }
  }

  if (totalNum != null) {
    headlineParagraphs.push(INTRO_COPY.otherSectorsInTables);
    if (agPlaceholder) headlineParagraphs.push(INTRO_COPY.agricultureOmitted);
  }

  const methodsParagraphs: string[] = [];
  const footnotes: string[] = [];
  if (!missingTotal) {
    methodsParagraphs.push(INTRO_COPY.methodsLead, INTRO_COPY.replica);
    if (report.transportDiagnostics?.hdv?.smoothingApplied || report.transportDiagnostics?.hdv?.activityRole === 'contextual') {
      footnotes.push(INTRO_COPY.hdvFootnote);
    }
    methodsParagraphs.push(INTRO_COPY.electricityNoProvider);
    if (registry?.suppressElectricityProviderName) {
      methodsParagraphs.push(INTRO_COPY.electricityProviderOmitted);
    }
    if (fillElec) methodsParagraphs.push(INTRO_COPY.partialElectricityFill);
    const combinedMt = wasteSector?.emissionsMt.value ?? null;
    if (combinedMt != null && Number.isFinite(combinedMt)) {
      if (isPublishableNumeric(wastewater) && (wwState === 'combined_scaled_other_year_in_total' || wwState === 'combined_same_year_in_total')) {
        methodsParagraphs.push(INTRO_COPY.wasteCombined(formatWholeMt(combinedMt)));
        if (wwState === 'combined_scaled_other_year_in_total' && wastewater.sourceYear != null) {
          const storedLabel =
            storedWw != null ? storedWw.toLocaleString('en-US', { maximumFractionDigits: 6 }) : formatComponentMt(wastewater.value);
          methodsParagraphs.push(
            INTRO_COPY.wastewaterScaled(
              formatComponentMt(wastewater.value),
              year,
              wastewater.sourceYear,
              storedLabel
            )
          );
          if (totalNum != null && totalNum > 0 && wastewater.value / totalNum < 0.001) {
            methodsParagraphs.push(INTRO_COPY.wastewaterShareLtTenth);
          }
        }
        methodsParagraphs.push(INTRO_COPY.facilityStacks);
      } else {
        methodsParagraphs.push(INTRO_COPY.wasteLandfillOnly(formatWholeMt(combinedMt)));
        methodsParagraphs.push(INTRO_COPY.facilityStacks);
      }
    }
  }

  const interpretParagraphs: string[] = [];
  if (!missingTotal) {
    interpretParagraphs.push(INTRO_COPY.interpretLead);
    if ((registry?.unresolvedBoundaries.length ?? 0) > 0) {
      interpretParagraphs.push(INTRO_COPY.interpretCampus);
    }
    interpretParagraphs.push(INTRO_COPY.interpretMissing, INTRO_COPY.twoLens, INTRO_COPY.comparisons, INTRO_COPY.laterSections);
  }

  const scopeBoxRows: ScopeBoxRow[] = [
    { id: 'place', label: 'Place', value: name },
    { id: 'year', label: 'Inventory year', value: String(year) },
    { id: 'lens', label: 'Accounting lens', value: ACCOUNTING_LENS_LABEL },
    { id: 'geography', label: 'Geography', value: 'Census place used by ClimatePlans' },
  ];
  if (!missingTotal) {
    scopeBoxRows.push({ id: 'included', label: 'Included sources', value: includedSourceLabels(report) });
  }
  if (totalNum != null) {
    scopeBoxRows.push({ id: 'total', label: 'Community total', value: `${formatWholeMt(totalNum)} MT CO₂e` });
  }
  if (popNum != null) {
    scopeBoxRows.push({ id: 'residents', label: 'Residents', value: Math.round(popNum).toLocaleString('en-US') });
  }
  if (perNum != null) {
    scopeBoxRows.push({ id: 'per_resident', label: 'Per resident', value: `${formatPerCapitaMt(perNum)} MT CO₂e` });
  }
  if (isPublishableNumeric(wastewater) && wastewater.sourceYear != null && wastewater.sourceYear !== year) {
    scopeBoxRows.push({
      id: 'wastewater',
      label: 'Wastewater',
      value: `Scaled estimate from the ${wastewater.sourceYear} inventory; included once in the waste and wastewater slice`,
    });
  }
  for (const boundary of registry?.unresolvedBoundaries ?? []) {
    scopeBoxRows.push({ id: `boundary_${boundary.id}`, label: 'Unresolved boundary', value: boundary.scopeBoxLine });
  }

  const blocksRendered: IntroductionBlockId[] = ['purpose'];
  if (!missingTotal) {
    blocksRendered.push('about', 'scope', 'headline', 'methods', 'interpret');
  } else {
    purposeParagraphs.push(INTRO_COPY.emptyInventory);
  }

  const allText = [
    ...purposeParagraphs,
    ...(blocksRendered.includes('about') ? aboutParagraphs : []),
    ...(blocksRendered.includes('scope') ? scopeParagraphs : []),
    ...(blocksRendered.includes('headline') ? headlineParagraphs : []),
    ...(blocksRendered.includes('methods') ? methodsParagraphs : []),
    ...(blocksRendered.includes('interpret') ? interpretParagraphs : []),
    ...footnotes,
  ];

  return {
    jurisdictionName: name,
    geofips: geofips ?? null,
    inventoryYear: year,
    accountingLensLabel: ACCOUNTING_LENS_LABEL,
    blocksRendered,
    purposeParagraphs,
    aboutParagraphs: blocksRendered.includes('about') ? aboutParagraphs : [],
    scopeParagraphs: blocksRendered.includes('scope') ? scopeParagraphs : [],
    headlineParagraphs: blocksRendered.includes('headline') ? headlineParagraphs : [],
    methodsParagraphs: blocksRendered.includes('methods') ? methodsParagraphs : [],
    interpretParagraphs: blocksRendered.includes('interpret') ? interpretParagraphs : [],
    footnotes,
    scopeBoxRows,
    scopeBoxSentences: [INTRO_COPY.scopeBoxCompleteness, INTRO_COPY.scopeBoxOps],
    findings,
    wastewaterState: wwState,
    unusualResidentDenominator: unusual,
    electricityLooksLikeFill: fillElec,
    missingCurrentYearTotal: missingTotal,
    wordCount: wordCount(allText),
    communityTotal,
    population,
    perResident,
    wastewater,
  };
}

export function introductionMemberText(view: IntroductionView): string {
  return [
    ...view.purposeParagraphs,
    ...view.aboutParagraphs,
    ...view.scopeParagraphs,
    ...view.headlineParagraphs,
    ...view.methodsParagraphs,
    ...view.interpretParagraphs,
    ...view.footnotes,
    ...view.scopeBoxRows.map((r) => `${r.label} ${r.value}`),
    ...view.scopeBoxSentences,
  ].join('\n');
}

export function quantitativeFindingCount(view: IntroductionView): number {
  return view.findings.filter((f) => f.kind === 'quantitative').length;
}
